import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Link,
} from "@mui/material";
import api from "../services/api";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const marginColor = (n) => (n > 0 ? "success.main" : n < 0 ? "error.main" : "text.primary");
const signed = (n) => `${n < 0 ? "−" : ""}$${money(Math.abs(n))}`;

export default function ContainerProfit() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSummary = useCallback(() => {
    setLoading(true); setError(null); setDetail(null);
    api.get("/accounting/container-profit", { params: { fromDate: fromDate || undefined, toDate: toDate || undefined } })
      .then((r) => setSummary(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Container Profit"))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const openBooking = useCallback((bookingNo) => {
    setLoading(true); setError(null);
    api.get("/accounting/container-profit", { params: { bookingNo } })
      .then((r) => setDetail(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to load booking detail"))
      .finally(() => setLoading(false));
  }, []);

  const cell = { py: 0.4, fontVariantNumeric: "tabular-nums" };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Container Profitability</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Profit per container and per booking. <strong>Revenue</strong> is exact (from sale invoice lines).
        <strong> Material</strong> is valued at weighted-average cost, <strong>Freight</strong> uses each freight
        company's per-container rate (set on the Freight Lane screen). Broker fees &amp; commissions are rolled up
        at the booking level (matched by PO). Read-only — nothing is changed.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="Sales from (optional)" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" size="small" label="To (optional)" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <Button variant="contained" onClick={loadSummary} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* BOOKING DETAIL */}
      {detail && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Booking {detail.bookingNo}</Typography>
            <Button size="small" onClick={() => setDetail(null)}>← Back to all bookings</Button>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Container</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Freight co.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Freight</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.containers.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary" }}>No container sales found for this booking.</TableCell></TableRow>
                )}
                {detail.containers.map((c, i) => (
                  <TableRow key={`${c.containerNo}-${i}`}>
                    <TableCell sx={{ py: 0.4 }}>
                      {c.containerNo}
                      {c.unvaluedWt > 0 && (
                        <Typography variant="caption" sx={{ display: "block", color: "warning.main" }}>
                          {money(c.unvaluedWt)} lb with no cost basis
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.4 }}>{c.company || "—"}</TableCell>
                    <TableCell align="right" sx={cell}>{money(c.revenue)}</TableCell>
                    <TableCell align="right" sx={cell}>{money(c.material)}</TableCell>
                    <TableCell align="right" sx={cell}>{money(c.freight)}</TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: marginColor(c.margin) }}>{signed(c.margin)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Booking rollup */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: "action.hover", maxWidth: 420 }}>
            <Row label="Sum of container margins" value={signed(detail.rollup.margin)} color={marginColor(detail.rollup.margin)} />
            <Row label="Less broker fees (PO-matched)" value={detail.rollup.broker ? `−$${money(detail.rollup.broker)}` : "$0.00"} />
            <Row label="Less commissions (approx)" value={detail.rollup.commission ? `−$${money(detail.rollup.commission)}` : "$0.00"} />
            <Divider sx={{ my: 1 }} />
            <Row label="Booking net profit" value={signed(detail.rollup.net)} color={marginColor(detail.rollup.net)} bold />
          </Box>
        </Paper>
      )}

      {/* SUMMARY (all bookings) */}
      {summary && !detail && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>All bookings (worst net first) — click a booking for its containers</Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Booking</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Cntrs</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Freight</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Broker</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Comm.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Net</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.bookings.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ color: "text.secondary" }}>No container sales in range.</TableCell></TableRow>
                )}
                {summary.bookings.map((b, i) => (
                  <TableRow key={`${b.bookingNo}-${i}`} hover>
                    <TableCell sx={{ py: 0.4 }}>
                      <Link component="button" underline="hover" onClick={() => openBooking(b.bookingNo)}>{b.bookingNo}</Link>
                    </TableCell>
                    <TableCell align="right" sx={cell}>{b.containers}</TableCell>
                    <TableCell align="right" sx={cell}>{money(b.revenue)}</TableCell>
                    <TableCell align="right" sx={cell}>{money(b.material)}</TableCell>
                    <TableCell align="right" sx={cell}>{money(b.freight)}</TableCell>
                    <TableCell align="right" sx={cell}>{b.broker ? money(b.broker) : "—"}</TableCell>
                    <TableCell align="right" sx={cell}>{b.commission ? money(b.commission) : "—"}</TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: marginColor(b.net) }}>{signed(b.net)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            "Cntrs" = containers with sales. A material with no purchase history has no cost basis and is excluded
            from Material (shown as a per-container warning), which can overstate that container's margin. Commissions
            are matched to a booking by invoice reference and are approximate — treat them as a guide, not a closing figure.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

function Row({ label, value, color, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
      <Typography variant="body2" sx={{ fontWeight: bold ? 800 : 400 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 800 : 600, color: color || "text.primary", fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Stack>
  );
}
